import { useLingui } from '@lingui/react/macro';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import type { LucideIcon } from 'lucide-react';
import { createContext, use, useState, type ReactNode } from 'react';

import { notifyApiError, useNotifier } from '@/shared/data';
import type { StepUpGrant } from '@/shared/http';
import { usePermissions, type PermissionResource } from '@/shared/permissions';
import { isEffect, isStepUp, stepUpGrantOf, type ActionPreview, type AnyScreenAction } from '@/shared/screen';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Button } from '@/shared/ui/button';
import { toast } from '@/shared/ui/toaster';

import { useRouterNavigation } from './navigation';
import { useOptionalStepUpAuthorize } from './step-up-port';
import { resolvePatternText, type PatternText } from './text';

/**
 * Uma ação do manifesto (U23) no lugar em que aparece: cabeçalho, barra, linha ou bloco. O `kind`
 * decide o comportamento visual (U39 §6): `read`/`navigate`/`draft` rodam na hora; `write` roda e
 * mostra o toast com **Desfazer** quando há `undo`; `destructive` pede confirmação com o verbo.
 */
export interface ActionSpec {
  readonly action: AnyScreenAction;
  /** Input fixo (o id do registro em foco, por exemplo); validado pelo zod da ação. */
  readonly input?: unknown;
  readonly icon?: LucideIcon;
  /** A ação primária da região: botão cheio (uma por região). */
  readonly primary?: boolean;
  readonly hidden?: boolean;
  /** `true` desabilita; texto desabilita e explica o motivo. */
  readonly disabled?: boolean | PatternText;
  /** Troca o rótulo do manifesto neste lugar. */
  readonly label?: PatternText;
  /** Verbo do botão de confirmação, em minúsculas ("excluir" → "Sim, excluir", U20.6). */
  readonly verb?: PatternText;
  /** Título do toast de sucesso (`write`/`destructive`). */
  readonly success?: PatternText;
  /** Estorno do `write`: aparece como **Desfazer** por 5 s (U39 §6). Recebe o retorno do `run`. */
  readonly undo?: (result: unknown) => unknown;
}

/** Monta um `ActionSpec` a partir da ação do manifesto. */
export function actionSpec(
  action: AnyScreenAction,
  extra: Omit<ActionSpec, 'action'> = {},
): ActionSpec {
  return { action, ...extra };
}

interface Pending {
  readonly spec: ActionSpec;
  readonly input: unknown;
  readonly preview: ActionPreview;
}

interface ActionRunner {
  /** Roda a ação com o comportamento do `kind`. `input` vence o `spec.input`. */
  run(spec: ActionSpec, input?: unknown): Promise<void>;
  /** Se quem vê tem a permissão da ação (semântica da tela, U37-1). */
  allowed(spec: ActionSpec): boolean;
}

const RunnerContext = createContext<ActionRunner | null>(null);

const UNDO_MS = 5000;

export interface ActionRunnerProviderProps {
  /** Chaves invalidadas depois de uma ação com efeito (e do Desfazer): a tela volta a mostrar o fio. */
  readonly invalidate?: readonly QueryKey[];
  readonly children: ReactNode;
}

/**
 * Executor único das ações da tela: permissão, confirmação de destrutiva, toast de sucesso com
 * Desfazer e erro com a regra do legado. Cabeçalho, blocos (via hospedeiro) e barras usam o mesmo.
 */
export function ActionRunnerProvider({ invalidate = [], children }: ActionRunnerProviderProps) {
  const { i18n, t } = useLingui();
  const queryClient = useQueryClient();
  const nav = useRouterNavigation();
  const notifier = useNotifier();
  const permissions = usePermissions();
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const authorize = useOptionalStepUpAuthorize();

  const labelOf = (spec: ActionSpec) => resolvePatternText(i18n, spec.label ?? spec.action.label);
  const refresh = () =>
    Promise.all(invalidate.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
  const undoing = (undo: NonNullable<ActionSpec['undo']>, result: unknown) => async () => {
    try {
      await undo(result);
      await refresh();
    } catch (error) {
      notifyApiError(notifier, error);
    }
  };

  const allowed = (spec: ActionSpec) => {
    const permission = spec.action.permission;
    return (
      !permission ||
      permissions.can(
        permission.resource as PermissionResource,
        permission.action,
        permission.strict === undefined ? undefined : { strict: permission.strict },
      )
    );
  };

  const parse = (spec: ActionSpec, input: unknown) => {
    const parsed = spec.action.input
      ? spec.action.input.safeParse(input ?? {})
      : { success: true as const, data: input };
    if (!parsed.success && import.meta.env.DEV) throw new Error(`ação ${spec.action.id}: input inválido`);
    return parsed;
  };

  const succeed = async (spec: ActionSpec, result: unknown) => {
    await refresh();
    const label = labelOf(spec);
    const title = spec.success ? resolvePatternText(i18n, spec.success) : t`Feito: ${label}`;
    const undo = spec.undo;
    toast.success(
      title,
      undo
        ? {
            duration: UNDO_MS,
            action: { label: t`Desfazer`, onClick: () => void undoing(undo, result)() },
          }
        : undefined,
    );
  };

  const execute = async (spec: ActionSpec, input: unknown, stepUp?: StepUpGrant) => {
    if (!allowed(spec)) return false;
    const parsed = parse(spec, input);
    if (!parsed.success) return false;
    try {
      const result: unknown = await spec.action.run({ input: parsed.data as unknown, nav, ...(stepUp ? { stepUp } : {}) });
      if (spec.action.kind === 'write' || spec.action.kind === 'destructive') await succeed(spec, result);
      return true;
    } catch (error) {
      notifyApiError(notifier, error);
      return false;
    }
  };

  // Step-up novo (`stepUp: { action }`): a prévia vira a Revisão do painel de senha + código, no lugar da confirmação;
  // o `run` só roda com a autorização. Fechar o painel não grava nada nem mostra toast.
  const executeWithGrant = async (spec: ActionSpec, input: unknown) => {
    const action = spec.action;
    const grant = stepUpGrantOf(action);
    if (!grant || !isEffect(action)) return;
    if (!authorize) throw new Error(`ação ${spec.action.id}: step-up sem StepUpProvider`);
    const parsed = parse(spec, input);
    if (!parsed.success) return;
    const preview = await action.preview(parsed.data ?? {});
    const verb = resolvePatternText(i18n, spec.verb ?? spec.action.label).toLocaleLowerCase(i18n.locale);
    try {
      const result = await authorize(
        {
          action: grant.action,
          resourceId: grant.resourceId?.(parsed.data),
          title: preview.title,
          description: preview.detail ?? t`Essa ação não pode ser desfeita.`,
          submitLabel: t`Sim, ${verb}`,
          destructive: spec.action.kind === 'destructive',
          // Como a confirmação de antes: o erro fica no painel aberto, para tentar de novo ou cancelar.
          errors: 'panel',
        },
        (stepUp) => Promise.resolve(spec.action.run({ input: parsed.data as unknown, nav, stepUp })),
      );
      await succeed(spec, result);
    } catch (error) {
      notifyApiError(notifier, error);
    }
  };

  const prepare = async (spec: ActionSpec, input: unknown) => {
    const parsed = parse(spec, input);
    if (!parsed.success) return;
    await spec.action.run({ input: parsed.data as unknown, nav });
  };

  const runner: ActionRunner = {
    allowed,
    run: async (spec, input) => {
      if (!allowed(spec)) return;
      const value = input ?? spec.input;
      if (stepUpGrantOf(spec.action)) {
        await executeWithGrant(spec, value);
        return;
      }
      // Step-up: o `run` só abre o diálogo de senha + MFA; confirmação e toast são dele (F2a).
      if (isStepUp(spec.action)) {
        await prepare(spec, value);
        return;
      }
      if (spec.action.kind === 'destructive') {
        const preview = await spec.action.preview(value ?? {});
        setPending({ spec, input: value, preview });
        return;
      }
      await execute(spec, value);
    },
  };

  const confirm = async () => {
    if (!pending) return;
    setBusy(true);
    const done = await execute(pending.spec, pending.input);
    setBusy(false);
    if (done) setPending(null);
  };

  const verb = pending
    ? resolvePatternText(i18n, pending.spec.verb ?? pending.spec.action.label).toLocaleLowerCase(
        i18n.locale,
      )
    : '';

  return (
    <RunnerContext value={runner}>
      {children}
      <AlertDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open && !busy) setPending(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pending?.preview.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {pending?.preview.detail ?? t`Essa ação não pode ser desfeita.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>{t`Não, cancelar`}</AlertDialogCancel>
            <Button variant="danger-solid" loading={busy} onClick={() => void confirm()}>
              {t`Sim, ${verb}`}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </RunnerContext>
  );
}

/** O executor do `ActionRunnerProvider` mais próximo. */
export function useActionRunner(): ActionRunner {
  const runner = use(RunnerContext);
  if (!runner) throw new Error('ação fora de um ActionRunnerProvider');
  return runner;
}

/** Com provider? `PageHeader` e `ListScreen` montam um só quando não há um acima. */
export function useHasActionRunner(): boolean {
  return use(RunnerContext) !== null;
}
